import { get as getConstants } from '@lumapps/constants';
import { AppId } from '@lumapps/constants/app';
import { getCurrentInstance, getInstanceById } from '@lumapps/instance/ducks/selectors';
import { Dispatch } from '@lumapps/redux/types';
import { Route } from '@lumapps/router';
import { RouterApi } from '@lumapps/router/api';
import { TranslationAPI } from '@lumapps/translations';
import { watchLinkActivation } from '@lumapps/utils/element/watchLinkActivation';

import { CommunityLinkRef } from '../../types';
import { prepareFetchUpdateCommunityLinkRef } from './fetchUpdatedCommunityLinkRef';
import { getCommunityRoute } from './getCommunityRoute';
import { updateCommunityLinkFromState } from './updateCommunityLinkFromState';

const updateFromStateThunk =
    (communityLinkRef: CommunityLinkRef, translateObject: TranslationAPI['translateObject']) =>
    (_: any, getState: GetBaseState) => {
        const state = getState();
        const instanceById = getInstanceById(state) || {};
        const { id: currentInstanceId } = getCurrentInstance(state);

        return updateCommunityLinkFromState(communityLinkRef, translateObject, {
            currentInstanceId,
            instanceById,
        });
    };

export interface ResolveCommunityLinkRouteOptions {
    languageHeader: string;
    currentInstanceId: string;
    isSpaceFeatureEnabled: boolean;
    homeRoute?: Route;
    dispatch: Dispatch;
    translateObject: TranslationAPI['translateObject'];
    isDisabled?: boolean;
    fetcher?: { fetch: () => Promise<CommunityLinkRef> };
}

/**
 * Resolve a community link ref to the route it targets: fetch the community rendering type and
 * instance details when missing, refresh the ref from the store, then resolve to the space view
 * for migrated communities, the legacy community view otherwise, or the home/not found page on
 * resolution error.
 *
 * Shared by `watchUpdateLink` (anchor activation) and `useCommunityLinkNavigation` (imperative
 * navigation) so both apply exactly the same resolution.
 */
export async function resolveCommunityLinkRoute(to: CommunityLinkRef, options: ResolveCommunityLinkRouteOptions) {
    const { applicationId } = getConstants();
    const { currentInstanceId, isSpaceFeatureEnabled, homeRoute, dispatch, translateObject } = options;
    const { renderingType, hasStaleData, instance } = to;

    const isCrossInstanceSlugMissing = instance?.id && instance?.id !== currentInstanceId && !instance?.slug;
    const isWebviewApp = applicationId === AppId.webview;

    // We don't need to update if:
    // - On webview app (we only need the id)
    // - No renderingType but Space FF is disabled
    const needUpdate =
        !isWebviewApp &&
        !options.isDisabled &&
        ((!renderingType && isSpaceFeatureEnabled) || isCrossInstanceSlugMissing || hasStaleData);

    const fetcher = options.fetcher ?? prepareFetchUpdateCommunityLinkRef({ to, ...options });

    // only updating if missing information
    const updatedTo = needUpdate ? await fetcher?.fetch() : to;
    // Get updated content link ref from store
    const updatedCommunityLinkRef: CommunityLinkRef = dispatch(updateFromStateThunk(updatedTo, translateObject));

    // Getting a route: either a space, a community, homepage or notFound depending of the updated params
    return getCommunityRoute(updatedCommunityLinkRef, { isSpaceFeatureEnabled, homeRoute });
}

export function watchUpdateLink(
    anchor: HTMLElement,
    to: CommunityLinkRef,
    options: Omit<ResolveCommunityLinkRouteOptions, 'fetcher'> & {
        homeRoute: Route;
        redirect: RouterApi['redirect'];
    },
) {
    const { redirect } = options;

    // Prepared once per watched anchor so repeated activations reuse the same in-flight request.
    const fetcher = prepareFetchUpdateCommunityLinkRef({ to, ...options });

    const onActivate = async (openInNewTab: boolean) => {
        const { route, routerData } = await resolveCommunityLinkRoute(to, { ...options, fetcher });

        if (!options.isDisabled) {
            redirect(route, {
                routerData,
                openInNewTab,
            });
        }
    };

    return watchLinkActivation({
        element: anchor,
        onActivate,
        isDisabled: options.isDisabled,
    });
}
